import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, defer, lastValueFrom, map, switchMap } from 'rxjs';
import slugify from 'slugify';

import { AuthService } from '@plugins/client/auth';
import {
  UPLOAD_FOLDER_FILE_TAG_NAME,
  UPLOAD_FOLDER_NAME,
} from '@plugins/shared/constants';
import { Attachment, Thumbnail } from '@plugins/shared/types/attachment';
import { randomString } from '@plugins/shared/utils/stringUtils/randomString';

import { readAsArrayBuffer } from '../private/helpers';

import { ImageResizeService } from './image-resize.service';

export class AttachmentEnvToken {
  attachmentSizeLimit!: number;
  imageFileSizeLimit!: number;
  imageContentTypes!: string[];
  imageThumbnailHeights!: { tiny: number; big: number };
  [key: string]: unknown;
}

@Injectable()
export class AttachmentBlobService {
  private readonly environment = inject(AttachmentEnvToken);
  private readonly authService = inject(AuthService);
  private readonly httpClient = inject(HttpClient);
  private readonly imageResizeService = inject(ImageResizeService);

  upload(file: File, folder = '', thumbnail = false): Observable<Attachment> {
    folder = folder.trim();
    // We automatically move all uploaded file to upload folder - this way we know which container to use when we generate full url ie BlobStoragePipe
    folder = folder.length
      ? `${UPLOAD_FOLDER_NAME}/${folder}`
      : UPLOAD_FOLDER_NAME;
    const randomPath = this.randomString();
    const attachment: Attachment = {
      title: file.name,
      path: this.generatePath({ randomPath, fileName: file.name, folder }),
      size: file.size,
      type: file.type,
    };

    return defer(async () => {
      const fileData = await readAsArrayBuffer(file);
      await this.putBlob(attachment.path, file.type, fileData);
      if (!file.type.match(/^image\/.+$/) || !thumbnail) {
        return attachment;
      }

      const origImage = await this.imageResizeService.file2Image(file);

      const tinyImage = await this.imageResizeService.resize(
        origImage,
        this.environment.imageThumbnailHeights.tiny,
        file.type,
      );
      const tinyThumb = {
        ...tinyImage.thumb,
        path: this.generatePath({
          randomPath,
          fileName: file.name,
          folder,
          suffix: 'tiny',
        }),
      };

      await this.putBlob(tinyThumb.path, tinyThumb.type, tinyImage.data);

      let bigThumb: Thumbnail = {
        height: origImage.height,
        width: origImage.width,
        size: attachment.size,
        path: attachment.path,
        type: attachment.type,
      };

      if (origImage.height > this.environment.imageThumbnailHeights.big) {
        const bigImage = await this.imageResizeService.resize(
          origImage,
          this.environment.imageThumbnailHeights.big,
          file.type,
        );
        bigThumb = {
          ...bigImage.thumb,
          path: this.generatePath({
            randomPath,
            fileName: file.name,
            folder,
            suffix: 'big',
          }),
        };
        await this.putBlob(bigThumb.path, bigThumb.type, bigImage.data);
      }

      attachment.image = {
        width: origImage.width,
        height: origImage.height,
        tiny: tinyThumb,
        big: bigThumb,
      };
      return attachment;
    });
  }

  fileFromAttachment(att: Attachment): Observable<File> {
    if (!this.authService.blob) {
      throw new Error('Blob storage access is not defined');
    }
    const { url, sas } = att.path.startsWith(`${UPLOAD_FOLDER_NAME}/`)
      ? this.authService.blob.upload
      : this.authService.blob.download;
    const attachmentUrl = `${url}/${att.path}?${sas}`;
    const fileExtension = att.path.match(/\..+$/)?.[0];
    const title = [att.title, fileExtension].filter(Boolean).join('.');
    return this.httpClient
      .get(attachmentUrl, { responseType: 'arraybuffer' })
      .pipe(map((buffer) => new File([buffer], title, { type: att.type })));
  }

  duplicateAttachment(
    att: Attachment,
    folder = '',
    thumbnail = false,
  ): Observable<Attachment> {
    return this.fileFromAttachment(att).pipe(
      switchMap((file) => this.upload(file, folder, thumbnail)),
    );
  }

  private async putBlob(
    blobPath: string,
    fileType: string,
    data: ArrayBuffer,
  ): Promise<null> {
    if (!this.authService.blob) {
      throw new Error('Blob storage access is not defined');
    }
    let headers = new HttpHeaders({
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-blob-content-type': fileType,
      'x-ms-meta-uploader': this.authService.userToken.sub,
    });
    if (blobPath.startsWith(`${UPLOAD_FOLDER_NAME}/`)) {
      // Add the upload lobby tag so that it can be found and cleared by storage life-cycle via blobIndexMatch (pulumi)
      headers = headers.append(
        'x-ms-tags',
        `${encodeURIComponent(UPLOAD_FOLDER_FILE_TAG_NAME)}=${encodeURIComponent(UPLOAD_FOLDER_NAME)}`,
      );
    }
    return lastValueFrom(
      this.httpClient.put<null>(
        `${this.authService.blob.upload.url}/${blobPath}?${this.authService.blob.upload.sas}`,
        data,
        { headers },
      ),
    );
  }

  private generatePath({
    fileName,
    suffix,
    randomPath,
    folder,
  }: {
    fileName: string;
    folder: string;
    randomPath: string;
    suffix?: string;
  }): string {
    const [baseName, extension] = fileName.split(/\.(?=[^.]+$)/);
    const finalName = [baseName, suffix].filter(Boolean).join('-');
    const slugified = `${slugify(finalName, {
      strict: true,
    })}.${extension}`;
    const fullPath = [folder, randomPath, slugified]
      .join('/')
      .replace(/\/{2,}/, '/');
    return fullPath;
  }

  private randomString(length = 64): string {
    return randomString(length);
  }
}
